// Sprint 35.0 — Bandeja de Salida (outbox) + sincronización automática.
// Regla: un POST que falla POR RED se guarda íntegro en IndexedDB y se reintenta al reconectar.
// Un error de servidor (HTTP 4xx/5xx) NO es falta de red: se descarta del outbox (ya fue atendido).

import api from "@/lib/api";
import { dbPut, dbDelete, dbGetAll, dbCount, cachePut, cacheGet } from "@/lib/offlineDb";

export const OUTBOX_EVENT = "pnv-outbox-changed";
export const SYNC_EVENT = "pnv-sync-state";

let _flushing = false;

function uuid() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "loc-" + Date.now() + "-" + Math.random().toString(16).slice(2);
}

function emitChanged() {
  try { window.dispatchEvent(new Event(OUTBOX_EVENT)); } catch {}
}
function emitSync(syncing) {
  try { window.dispatchEvent(new CustomEvent(SYNC_EVENT, { detail: { syncing } })); } catch {}
}

// Detecta si el error de axios es por FALTA DE RED (sin respuesta del servidor).
export function isNetworkError(err) {
  if (typeof navigator !== "undefined" && navigator.onLine === false) return true;
  return !!err && !err.response;
}

// Encola un POST fallido. `label` es un texto para el operador.
export async function enqueue({ url, method = "post", body, kind, label, folio_local }) {
  const record = { id: uuid(), ts: Date.now(), url, method, body, kind, label: label || kind, folio_local: folio_local || null };
  await dbPut(record);
  emitChanged();
  return record;
}

export async function pendingCount() {
  try { return await dbCount(); } catch { return 0; }
}

export async function listPending() {
  try {
    const all = await dbGetAll();
    return all.sort((a, b) => a.ts - b.ts);
  } catch { return []; }
}

// Envía TODOS los pendientes en orden cronológico. Se detiene ante el primer error de red.
export async function flush() {
  if (_flushing) return { sent: 0, remaining: await pendingCount() };
  if (typeof navigator !== "undefined" && navigator.onLine === false) return { sent: 0, remaining: await pendingCount() };
  // El guard debe ser SÍNCRONO: se fija antes de cualquier await para evitar que dos
  // disparos concurrentes (evento online + reintento periódico) envíen el mismo registro 2 veces.
  _flushing = true;
  emitSync(true);
  let sent = 0;
  try {
    const items = await listPending();
    if (items.length === 0) return { sent: 0, remaining: 0 };
    for (const it of items) {
      try {
        await api.request({ url: it.url, method: it.method || "post", data: it.body });
        await dbDelete(it.id);
        sent += 1;
        emitChanged();
      } catch (err) {
        if (isNetworkError(err)) {
          // Sigue sin red: conserva éste y el resto, reintentará al reconectar.
          break;
        }
        // Error de negocio/servidor (ej. ya sincronizado, stock, ticket ya cobrado):
        // se considera atendido y se descarta para no bloquear la cola.
        await dbDelete(it.id);
        emitChanged();
      }
    }
  } finally {
    _flushing = false;
    emitSync(false);
    emitChanged();
  }
  return { sent, remaining: await pendingCount() };
}

// Arranca la sincronización automática: al recuperar señal y al cargar la app.
let _autoStarted = false;
export function initAutoSync() {
  if (_autoStarted) return;
  _autoStarted = true;
  const trigger = () => { flush().catch(() => {}); };
  window.addEventListener("online", trigger);
  // Reintento periódico defensivo (por si el evento online no dispara en algún dispositivo).
  setInterval(() => { if (navigator.onLine) trigger(); }, 20000);
  // Intento inicial (pendientes de sesiones anteriores).
  setTimeout(trigger, 1500);
}

// ---- Cache de tarifas y patio por sucursal (para cobro offline) ----
export const saveSnapshot = (sid, data) => cachePut(`snapshot:${sid}`, data);
export const getSnapshot = (sid) => cacheGet(`snapshot:${sid}`);
export const savePatio = (sid, autos) => cachePut(`patio:${sid}`, autos || []);
export const getPatio = (sid) => cacheGet(`patio:${sid}`);
export async function patioRemove(sid, idTicket) {
  const autos = (await getPatio(sid)) || [];
  await savePatio(sid, autos.filter((a) => a.id_ticket !== idTicket));
}
